import { CreateDrawerFormProps, FormDrawer, FormDrawerContent, FormDrawerContentProps } from "@dgs/core";
import { FormikErrors, useFormikContext } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	AssignableTravelSegmentType,
	TravelDirection,
	TravelSegmentFormValues,
} from "../../../../../shared/types/travelSegment";
import { TravelSegmentCarFormFields, emptyCarFormValues } from "./TravelSegmentCarFormFields";
import { TravelSegmentFlightFormFields, emptyFlightFormValues } from "./TravelSegmentFlightFormFields";
import { TravelSegmentTrainFormFields, emptyTrainFormValues } from "./TravelSegmentTrainFormFields";
import { TravelSegmentTypeFormSelect } from "./TravelSegmentTypeFormSelect";

const useValidate = (direction: TravelDirection) => {
	const { t } = useTranslation();

	return (values: TravelSegmentFormValues) => {
		switch (values.type) {
			case "car": {
				const errors: FormikErrors<typeof values> = {};

				if (direction === "arrival") {
					if (!values.arrivalDate) {
						errors.arrivalDate = t("Field is required");
					}
					if (!values.arrivalTime) {
						errors.arrivalTime = t("Field is required");
					}
				}

				if (direction === "departure") {
					if (!values.departureDate) {
						errors.departureDate = t("Field is required");
					}
					if (!values.departureTime) {
						errors.departureTime = t("Field is required");
					}
				}

				if (!values.identifier) {
					errors.identifier = t("Field is required");
				}
				return errors;
			}
			case "train":
			case "flight": {
				const errors: FormikErrors<typeof values> = {};
				if (!values.departureLocation) {
					errors.departureLocation = t("Field is required");
				}
				if (!values.arrivalLocation) {
					errors.arrivalLocation = t("Field is required");
				}
				if (!values.arrivalDate) {
					errors.arrivalDate = t("Field is required");
				}
				if (!values.arrivalTime) {
					errors.arrivalTime = t("Field is required");
				}
				if (!values.departureDate) {
					errors.departureDate = t("Field is required");
				}
				if (!values.departureTime) {
					errors.departureTime = t("Field is required");
				}
				return errors;
			}
		}
	};
};

interface TravelSegmentDrawerContentProps extends FormDrawerContentProps {
	direction: TravelDirection;
	availableTypes: AssignableTravelSegmentType[];
}

const TravelSegmentDrawerContent: FC<TravelSegmentDrawerContentProps> = ({ direction, availableTypes, ...props }) => {
	const { t } = useTranslation();
	const { values } = useFormikContext<TravelSegmentFormValues>();

	return (
		<FormDrawerContent {...props}>
			{availableTypes.length > 1 && (
				<TravelSegmentTypeFormSelect name="type" label={t("Type")} availableTypes={availableTypes} />
			)}
			{values.type === "flight" && <TravelSegmentFlightFormFields />}
			{values.type === "car" && <TravelSegmentCarFormFields direction={direction} />}
			{values.type === "train" && <TravelSegmentTrainFormFields />}
		</FormDrawerContent>
	);
};

interface TravelSegmentCreateDrawerProps extends CreateDrawerFormProps<TravelSegmentFormValues> {
	direction: TravelDirection;
	availableTypes: AssignableTravelSegmentType[];
}

export const TravelSegmentCreateDrawer: FC<TravelSegmentCreateDrawerProps> = ({
	direction,
	availableTypes,
	...drawerProps
}) => {
	const { t } = useTranslation();
	const validate = useValidate(direction);
	availableTypes = availableTypes.filter((i) => i !== "shuttle");
	const initialValues = useMemo(() => {
		if (availableTypes.includes("car")) {
			return emptyCarFormValues();
		} else if (availableTypes.includes("flight")) {
			return emptyFlightFormValues();
		} else {
			return emptyTrainFormValues();
		}
	}, [availableTypes]);

	return (
		<FormDrawer
			{...drawerProps}
			initialValues={initialValues}
			title={t("Create travel segment")}
			validate={validate}
			renderForm={(props) => (
				<TravelSegmentDrawerContent {...props} direction={direction} availableTypes={availableTypes} />
			)}
		/>
	);
};
